<script lang="ts">
  import { api } from '$shared/api';
  import { CREATOR_TRACK_SUBMISSION } from '$lib/features';
  import type { Duel } from '$types/index';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import DuelCard from '$components/shared/DuelCard.svelte';
  import SeasonBanner from '$components/shared/SeasonBanner.svelte';

  /** Creator-lens duel row + the home-feed extras the API sends. */
  type HomeDuel = Duel & { my_song_count?: number; my_song_won?: boolean };

  /**
   * Raw `my_duels` row — the home endpoint predates the list contract, so
   * entry counts arrive as `track_count` and `entry_cap` may be absent.
   * Normalized into the `Duel` shape in loadHome() below.
   */
  type HomeDuelRow = Omit<HomeDuel, 'entry_count' | 'entry_cap'> & {
    entry_count?: number;
    entry_cap?: number;
    track_count?: number;
  };

  interface HomeData {
    my_duels: HomeDuelRow[];
    welcome_back: { last_duel_id: number; last_duel_name: string; days_away: number } | null;
  }

  let myDuels = $state<HomeDuel[]>([]);
  let welcomeBack = $state<HomeData['welcome_back']>(null);
  let loading = $state(true);
  let error = $state('');

  $effect(() => {
    loadHome();
  });

  async function loadHome() {
    loading = true;
    try {
      const res = await api.get<HomeData>('/users/current/home');
      myDuels = (res.my_duels ?? []).map((r) => ({
        ...r,
        entry_count: r.entry_count ?? r.track_count ?? 0,
        entry_cap: r.entry_cap ?? 0,
      }));
      welcomeBack = res.welcome_back ?? null;
    } catch (err) {
      error = (err as Error).message;
    } finally {
      loading = false;
    }
  }

  /** "Your song WON" only means anything once the duel has actually ended. */
  function songBadge(d: HomeDuel): { label: string; won?: boolean } {
    if (d.my_song_won && d.status === 'ended') return { label: 'Your song WON', won: true };
    return { label: 'Your song is in' };
  }
</script>

<div>
  <!-- Rendered independent of the Home data load so it shows ASAP -->
  <SeasonBanner />

  {#if loading}
    <LoadingSkeleton lines={8} />
  {:else if error}
    <p class="msg--error">{error}</p>
  {:else}
    {#if welcomeBack}
      <div class="card welcome-back-card">
        <h3>Welcome back!</h3>
        <p>
          You've been away {welcomeBack.days_away} days. Your last duel "{welcomeBack.last_duel_name}" has ended.
        </p>
        <div class="welcome-back-actions">
          <a href="#/duels/{welcomeBack.last_duel_id}/result" class="btn btn-secondary btn-sm">See Results</a>
          <a href="#/duels" class="btn btn-primary btn-sm">Browse Duels</a>
        </div>
      </div>
    {/if}

    <!-- With creator submission off (decision 114) an empty section has no on-ramp,
         so it only shows when the viewer already has songs in duels. -->
    {#if CREATOR_TRACK_SUBMISSION || myDuels.length > 0}
      <section>
        <h2>Duels Featuring Your Songs</h2>
        {#if myDuels.length === 0}
          <!-- No duels feature the viewer's songs — presumably they have no catalog
             songs yet, so point them at the creator on-ramp. -->
          <a href="#/catalog/add" class="card add-song-cta">
            <h3 class="add-song-cta__title">Add a song to get in the game</h3>
            <p class="add-song-cta__text">
              Players claim catalog songs into duels. Add a YouTube song and it can start collecting crowns.
            </p>
            <span class="btn btn-primary btn-pill">Add a Song</span>
          </a>
        {:else}
          <div class="grid-cards">
            {#each myDuels as duel (duel.id)}
              <DuelCard {duel} mySongBadge={songBadge(duel)} />
            {/each}
          </div>
        {/if}
      </section>
    {/if}
  {/if}
</div>

<style>
  section {
    margin-bottom: 2rem;
  }

  /* Add-a-song CTA (creator on-ramp when no duels feature the viewer's songs) */
  .add-song-cta {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.5rem;
    padding: 1.75rem 1.5rem;
    border-color: rgba(255, 45, 120, 0.3);
  }
  .add-song-cta:hover {
    border-color: var(--pink);
  }
  .add-song-cta__title {
    margin: 0;
    font-size: 1.1rem;
    color: var(--text);
  }
  .add-song-cta__text {
    margin: 0;
    color: var(--text-secondary);
    font-size: 0.875rem;
    max-width: 32rem;
  }

  .welcome-back-card {
    text-align: center;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border-left: 3px solid var(--cyan);
  }
  .welcome-back-card h3 {
    margin: 0 0 0.5rem;
    color: var(--cyan);
  }
  .welcome-back-card p {
    color: var(--text-secondary);
    font-size: 0.9rem;
    margin: 0 0 1rem;
  }
  .welcome-back-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: center;
  }
</style>
